import { useCallback } from 'react';
import { getConfig } from '../config';

export interface ImageUrlResult {
    url: string | null;
    error: 'not_found' | 'unavailable' | 'invalid_response' | null;
}

type CooldownEntry = {
    eventId: string;
    state: 'pending' | 'accepted';
    expiresAt: number;
};

const COOLDOWN_PREFIX = 'siteFront.scoreCooldown.v1:';
const COOLDOWN_MS = 30 * 60 * 1000;
const PENDING_MS = 15000;
const REQUEST_TIMEOUT_MS = 5000;
const MAX_ATTEMPTS = 2;
const memoryCooldowns = new Map<string, CooldownEntry>();
const inFlightEvents = new Map<string, Promise<boolean>>();
let fallbackSequence = 0;

function getCooldown(key: string, now: number): CooldownEntry | null {
    const memoryEntry = memoryCooldowns.get(key);
    if (memoryEntry && memoryEntry.expiresAt > now) return memoryEntry;
    if (memoryEntry) memoryCooldowns.delete(key);

    try {
        const raw = window.localStorage.getItem(key);
        if (!raw) return null;
        const entry = JSON.parse(raw) as CooldownEntry;
        if ((entry.state !== 'pending' && entry.state !== 'accepted')
            || typeof entry.eventId !== 'string' || !Number.isFinite(entry.expiresAt)
            || entry.expiresAt <= now) {
            window.localStorage.removeItem(key);
            return null;
        }
        memoryCooldowns.set(key, entry);
        return entry;
    } catch {
        return null;
    }
}

function setCooldown(key: string, entry: CooldownEntry): void {
    memoryCooldowns.set(key, entry);
    try {
        window.localStorage.setItem(key, JSON.stringify(entry));
    } catch {
        // In-memory suppression still covers the current page when storage is unavailable.
    }
}

function clearCooldown(key: string, eventId: string): void {
    if (memoryCooldowns.get(key)?.eventId === eventId) memoryCooldowns.delete(key);
    try {
        const entry = JSON.parse(window.localStorage.getItem(key) || 'null') as CooldownEntry | null;
        if (entry?.eventId === eventId) window.localStorage.removeItem(key);
    } catch {
        // Storage may be unavailable; the memory entry was cleared above.
    }
}

function createEventId(): string {
    const cryptoApi = window.crypto;
    if (typeof cryptoApi?.randomUUID === 'function') return cryptoApi.randomUUID().replace(/-/g, '');
    if (typeof cryptoApi?.getRandomValues === 'function') {
        const bytes = cryptoApi.getRandomValues(new Uint8Array(16));
        return Array.from(bytes, byte => byte.toString(16).padStart(2, '0')).join('');
    }
    fallbackSequence += 1;
    return `${Date.now().toString(36)}-${fallbackSequence.toString(36)}-${Math.random().toString(36).slice(2)}`;
}

type ScoreAction = 'view' | 'zoom' | 'bookmark';

async function sendScoreEvent(apiUrl: string, key: string, imageId: string, action: ScoreAction, eventId: string): Promise<boolean> {
    const expectedPoints = action === 'view' ? 1 : action === 'bookmark' ? 5 : 3;
    const body = JSON.stringify({ image_id: imageId, action, event_id: eventId });

    for (let attempt = 0; attempt < MAX_ATTEMPTS; attempt += 1) {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
        try {
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body,
                signal: controller.signal,
            });
            let data: { success?: boolean; duplicate?: boolean; added?: number } | null = null;
            try {
                data = await response.json();
            } catch {
                data = null;
            }
            const accepted = response.ok && data?.success === true
                && ((data.duplicate === true && data.added === 0) || (data.duplicate !== true && data.added === expectedPoints));
            if (accepted) {
                if (action !== 'bookmark') setCooldown(key, { eventId, state: 'accepted', expiresAt: Date.now() + COOLDOWN_MS });
                window.dispatchEvent(new CustomEvent('image-score-changed', { detail: { imageId, action } }));
                return true;
            }
            if (response.status < 500 || attempt + 1 >= MAX_ATTEMPTS) break;
        } catch {
            if (attempt + 1 >= MAX_ATTEMPTS) break;
        } finally {
            window.clearTimeout(timeout);
        }
        await new Promise(resolve => window.setTimeout(resolve, 250));
    }

    if (action !== 'bookmark') clearCooldown(key, eventId);
    return false;
}

export const useScore = () => {
    const { apiHost } = getConfig();
    const apiUrl = `${apiHost}/score_api.php`;

    const sendAction = useCallback((imageId: string, action: ScoreAction): Promise<boolean> => {
        if (!/^[1-9][0-9]{0,17}$/.test(String(imageId)) || !['view', 'zoom', 'bookmark'].includes(action)) {
            return Promise.resolve(false);
        }
        const normalizedId = String(imageId);
        const eventId = createEventId();
        const key = `${COOLDOWN_PREFIX}${normalizedId}:${action}${action === 'bookmark' ? ':' + eventId : ''}`;
        const existingRequest = inFlightEvents.get(key);
        if (existingRequest) return existingRequest;

        const existingCooldown = action !== 'bookmark' && getCooldown(key, Date.now());
        if (existingCooldown) return Promise.resolve(false);

        if (action !== 'bookmark') setCooldown(key, { eventId, state: 'pending', expiresAt: Date.now() + PENDING_MS });
        const request = sendScoreEvent(apiUrl, key, normalizedId, action, eventId);
        inFlightEvents.set(key, request);
        void request.finally(() => {
            if (inFlightEvents.get(key) === request) inFlightEvents.delete(key);
        });
        return request;
    }, [apiUrl]);

    const fetchImageUrl = useCallback(async (imageId: string): Promise<ImageUrlResult> => {
        const controller = new AbortController();
        const timeout = window.setTimeout(() => controller.abort(), 15000);
        try {
            const response = await fetch(`${apiUrl}?id=${encodeURIComponent(imageId)}`, { signal: controller.signal });
            if (response.ok) {
                const data = await response.json();
                return data.url
                    ? { url: String(data.url), error: null }
                    : { url: null, error: 'invalid_response' };
            }
            return { url: null, error: response.status === 404 ? 'not_found' : 'unavailable' };
        } catch (error) {
            console.error('Failed to fetch image URL:', error);
            return { url: null, error: 'unavailable' };
        } finally {
            window.clearTimeout(timeout);
        }
    }, [apiUrl]);

    return { sendAction, fetchImageUrl };
};
